import { getApiClientRuntime } from '@saniplus/api-client';
import type { Task } from '@saniplus/contracts';
import { useCallback } from 'react';

import { newClientPunchUuid, usePunchOutMutation } from '../offline';
import { useGetMyTasksQuery } from './api';
import { blockingTasks, pendingTasks } from './lib/blocking';

export interface TaskGate {
  /**
   * The PENDING tasks of the current-or-next shift, in server order — the
   * rows themselves, because the home screen shows titles rather than a
   * count (T-14, 2026-09-03 device report). `blockingCount` is derived from
   * this same array by every consumer, so the two can never disagree.
   */
  readonly pending: readonly Task[];
  /** Every task of that shift, closed ones included — what the home line lists. */
  readonly tasks: readonly Task[];
  readonly blockingCount: number;
  /** The override punch OUT (T-07): rejects to keep its sheet open. */
  readonly overridePunchOut: (reason: string) => Promise<void>;
}

/**
 * The one seam between the punch screen and this feature (P3 §6: "wire it
 * into the punch screen with a light selector/hook; do NOT restructure the
 * punch feature, compose in"). `PunchPage.tsx` calls this and spreads three
 * values into `HomeScreen`; nothing in `features/punch` imports `features/tasks`
 * beyond that one call plus the two presentational components `HomeScreen`
 * composes in (`HomeTasksLine`, `PunchOutGate`).
 *
 * `overridePunchOut` deliberately does **not** go through
 * `features/offline`'s durable IndexedDB queue: that queue's record shape
 * (`QueuedPunch`) is out of scope for this phase (P3 is UI-only; the actual
 * server-side gate is P4), and this action is only ever reachable while a
 * worker is online in the first place (task data is online-only, T-09), so
 * the durability gap is narrow — a network failure here surfaces as a
 * "not saved" refusal the worker can retry immediately, rather than as a
 * silently dropped punch. Folding it into the queue (so it also survives an
 * app kill mid-flight) is natural follow-up work for P4, which already owns
 * `apps/api/src/modules/punch/` and would touch `QueuedPunch` at the same
 * time.
 * ponytail: bypasses the durable offline queue for this one override path;
 * fold `taskOverrideReason` into `QueuedPunch`/`EnqueueArgs` in P4 if the
 * team decides this escape hatch must survive an app kill mid-flight too.
 */
export function useTaskGate(): TaskGate {
  const tasks = useGetMyTasksQuery().data ?? [];
  const [punchOut] = usePunchOutMutation();

  const overridePunchOut = useCallback(
    async (reason: string): Promise<void> => {
      await punchOut({
        clientPunchUuid: newClientPunchUuid(),
        deviceUuid: getApiClientRuntime().getDeviceUuid(),
        gpsStatus: 'unavailable',
        taskOverrideReason: reason,
      }).unwrap();
    },
    [punchOut],
  );

  return {
    pending: pendingTasks(tasks),
    tasks,
    blockingCount: blockingTasks(tasks).length,
    overridePunchOut,
  };
}
